import { z } from 'zod';

/** Batas banner yang tayang bersamaan di carousel beranda. */
export const BANNER_MAX_LIVE = 5;

/**
 * Tautan internal (`/katalog`) atau https penuh. `//host` ditolak karena
 * browser membacanya sebagai tautan ke domain lain.
 */
const bannerHrefSchema = z
  .string()
  .trim()
  .min(1, 'Link tujuan wajib diisi')
  .max(500)
  .refine(
    (v) => (v.startsWith('/') && !v.startsWith('//')) || /^https:\/\/[^\s]+$/.test(v),
    'Link harus diawali / (halaman situs) atau https://',
  );

const idSchema = z.string().min(1).max(64);
const dateSchema = z.coerce.date().nullable();

const bannerFields = {
  /** Nama untuk admin, mis. "Promo 9.9"; tidak tampil di situs. */
  title: z.string().trim().min(1, 'Nama banner wajib diisi').max(120),
  /** Teks alternatif gambar untuk pembaca layar dan SEO. */
  alt: z.string().trim().min(1, 'Teks alternatif wajib diisi').max(240),
  mediaId: idSchema,
  /** Kosong berarti layar HP memakai gambar desktop. */
  mobileMediaId: idSchema.nullable(),
  href: bannerHrefSchema,
  /** Klik membuka chat Imma; href jadi cadangan bila Imma tidak tersedia. */
  opensImma: z.boolean(),
  isActive: z.boolean(),
  /** Kosong berarti langsung tayang / tanpa batas akhir. */
  startsAt: dateSchema,
  endsAt: dateSchema,
};

function scheduleValid(v: {
  startsAt?: Date | null | undefined;
  endsAt?: Date | null | undefined;
}) {
  return !v.startsAt || !v.endsAt || v.endsAt > v.startsAt;
}
const scheduleMessage = {
  message: 'Waktu selesai harus setelah waktu mulai',
  path: ['endsAt'],
};

export const bannerInputSchema = z
  .object({
    ...bannerFields,
    mobileMediaId: bannerFields.mobileMediaId.default(null),
    opensImma: bannerFields.opensImma.default(false),
    isActive: bannerFields.isActive.default(true),
    startsAt: bannerFields.startsAt.default(null),
    endsAt: bannerFields.endsAt.default(null),
  })
  .refine(scheduleValid, scheduleMessage);

/** Jadwal dicek ulang di server setelah digabung dengan nilai lama. */
export const bannerUpdateSchema = z
  .object(bannerFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Tidak ada perubahan yang dikirim' })
  .refine(scheduleValid, scheduleMessage);

export const bannerParamsSchema = z.object({ id: idSchema });

/** Urutan baru seluruh banner, dari yang tampil pertama. */
export const bannerReorderSchema = z.object({ ids: z.array(idSchema).min(1).max(200) });

export interface BannerWindow {
  isActive: boolean;
  startsAt: Date | null;
  endsAt: Date | null;
}

export function isBannerLive(b: BannerWindow, now: Date = new Date()): boolean {
  return b.isActive && (!b.startsAt || b.startsAt <= now) && (!b.endsAt || b.endsAt > now);
}

export type BannerState = 'LIVE' | 'SCHEDULED' | 'ENDED' | 'INACTIVE';

export function bannerState(b: BannerWindow, now: Date = new Date()): BannerState {
  if (!b.isActive) return 'INACTIVE';
  if (b.endsAt && b.endsAt <= now) return 'ENDED';
  if (b.startsAt && b.startsAt > now) return 'SCHEDULED';
  return 'LIVE';
}

/**
 * Jumlah terbesar banner aktif yang tayang pada saat yang sama, dihitung dari
 * `from` ke depan (masa lalu tidak relevan). Sapuan titik mulai/selesai:
 * selesai diproses sebelum mulai pada waktu yang sama karena rentangnya
 * setengah terbuka [mulai, selesai).
 */
export function maxConcurrentBanners(banners: BannerWindow[], from: Date = new Date()): number {
  const events: [number, number][] = [];
  for (const b of banners) {
    if (!b.isActive) continue;
    const end = b.endsAt ? b.endsAt.getTime() : Infinity;
    if (end <= from.getTime()) continue;
    const start = Math.max(b.startsAt ? b.startsAt.getTime() : -Infinity, from.getTime());
    events.push([start, 1], [end, -1]);
  }
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let current = 0;
  let max = 0;
  for (const [, delta] of events) {
    current += delta;
    max = Math.max(max, current);
  }
  return max;
}

export type BannerInput = z.infer<typeof bannerInputSchema>;
export type BannerUpdate = z.infer<typeof bannerUpdateSchema>;
export type BannerReorder = z.infer<typeof bannerReorderSchema>;
